'use strict';

const { BaseWidget } = require('./BaseWidget');
const { h } = require('./dom');
const { BX } = require('./theme');
const { formatCurrency, maskAadhaar, formatDateTime } = require('./format');
const rd = require('./rd');

const OPERATIONS = [
  { id: 'withdrawal', label: 'Cash Withdrawal', icon: '₹', needsAmount: true },
  { id: 'balance', label: 'Balance Enquiry', icon: '≣', needsAmount: false },
  { id: 'minStatement', label: 'Mini Statement', icon: '≡', needsAmount: false },
  { id: 'purchase', label: 'Aadhaar Pay', icon: '✋', needsAmount: true },
];

/** Mirrors webpayment's composables/useGeolocation.ts exactly — AEPS requires
 *  the agent's Latitude/Longitude on every call (NSDL requirement), so a
 *  failure here blocks the flow rather than silently sending 0,0. */
function getLocation() {
  return new Promise((resolve) => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      resolve({ ok: false, message: "Location services aren't available on this device" });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ ok: true, lat: String(pos.coords.latitude), lng: String(pos.coords.longitude) }),
      (err) => {
        const message = err.code === err.PERMISSION_DENIED
          ? 'Location permission denied — allow location access to continue'
          : 'Could not determine your location — check GPS/location settings and retry';
        resolve({ ok: false, message });
      },
      { timeout: 8000, maximumAge: 60000, enableHighAccuracy: true },
    );
  });
}

/**
 * Maps a raw /aeps/* response into a normalized result — ported from
 * webpayment's stores/aeps.ts#mapAepsResponse. The outer envelope's
 * statusCode is NOT what determines success for a transaction call; NSDL's
 * own ResponseCode nested under `data` is (confirmed against
 * aeps.txn.controller.js). If `data` itself is missing, NSDL never
 * responded synchronously — treat as PENDING, not a hard failure.
 */
function mapAepsResponse(res, aadhaar) {
  const d = res?.data;
  const nsdlResponded = !!d;
  const status = !nsdlResponded ? 'PENDING' : d.ResponseCode === '00' ? 'SUCCESS' : 'FAILED';
  return {
    status,
    reason: status === 'SUCCESS' ? null : (d?.ResponseStatus || res?.message || 'Transaction could not be completed'),
    balance: d?.Balance ?? null,
    statementLines: typeof d?.Ministatement === 'string' ? d.Ministatement.split(/\r?\n/).filter(Boolean) : null,
    stan: d?.STAN ?? '',
    referenceNo: d?.ReferenceNo ?? '',
    retailerReferenceNumber: d?.RetailerReferenceNumber ?? '',
    authCode: d?.AuthCode ?? null,
    aadhaarMasked: d?.AadharNo ?? maskAadhaar(aadhaar),
  };
}

/**
 * AepsWidget — daily agent authentication gate, then Cash Withdrawal /
 * Balance Enquiry / Mini Statement / Aadhaar Pay against the customer's
 * Aadhaar-linked bank account, using a local RD biometric service and the
 * real NSDL IIN bank list. Mirrors webpayment's pages/merchant/aeps/* flow
 * and stores/aeps.ts.
 *
 * const widget = new AepsWidget(client, { onSuccess, onError });
 * widget.mount('#bbx-aeps').start();
 */
class AepsWidget extends BaseWidget {
  start() {
    this._params = {};
    this._banks = null;
    this.screenAuthGate();
    return this;
  }

  /** Fetches the AEPS bank (IIN) list once and caches it — shared across every
   *  operation's "Select Bank" dropdown, same as webpayment's store. */
  async fetchBanks() {
    if (this._banks) return this._banks;
    try {
      const res = await this.client.aeps.banks();
      this._banks = Array.isArray(res?.data) ? res.data : [];
    } catch (e) {
      this._banks = [];
    }
    return this._banks;
  }

  async screenAuthGate() {
    this.setHeader({ title: 'AEPS Services', subtitle: 'Aadhaar-enabled banking', showBack: false });
    this.renderLoading('Checking agent authentication…');

    try {
      const res = await this.client.aeps.authStatus();
      const authenticated = res?.data?.authenticated === true;
      if (authenticated) return this.screenMenu();

      // "not authenticated yet" (statusCode '00', authenticated: false) is the
      // normal first-run case — show the plain gate card. Anything else
      // (merchant not found/authorized, bad credentials, etc.) is a real
      // error and must be surfaced, not silently treated as "just needs
      // today's auth" — the caller would otherwise see a generic gate with
      // no indication the terminal itself is misconfigured.
      const isRealError = res?.statusCode != null && res.statusCode !== '00';
      if (isRealError) this.fail(res, res.message);
      this.screenGateCard(isRealError ? res.message : null);
    } catch (e) {
      this.fail(e);
      this.screenGateCard(this.errorMessage(e, 'Could not check authentication status'));
    }
  }

  screenGateCard(errorMsg) {
    this.setHeader({ title: 'AEPS Services', subtitle: 'Aadhaar-enabled banking', showBack: false });
    this.showScreen(
      h('div', {}, [
        errorMsg ? this.banner('error', errorMsg) : null,
        h('div', { class: 'bbx-card', style: { textAlign: 'center' } }, [
          h('div', { class: 'bbx-status-icon', style: { background: BX.primary }, text: '🔒' }),
          h('p', { class: 'bbx-status-title', text: 'Complete Agent Authentication' }),
          h('p', { class: 'bbx-status-sub', text: 'Authenticate this terminal with the agent’s fingerprint once to unlock AEPS services. Valid for 24 hours.' }),
          h('button', { class: 'bbx-btn', text: 'Authenticate Now', onClick: () => this.screenAgentAuth() }),
        ]),
      ]),
    );
  }

  /** Agent auth (reqAuth) shares the same payload shape as every other AEPS
   *  call, but with no bank selected (agent-auth.vue's CustomerBankForm uses
   *  with-bank="false") and its own outer-envelope success check — reqAuth's
   *  statusCode IS the real success signal here, unlike the NSDL-nested
   *  ResponseCode transaction calls use. */
  screenAgentAuth(errorMsg) {
    this.setHeader({ title: 'Agent Authentication', showBack: true, onBack: () => this.screenGateCard() });

    const aadhaarField = this.field({ label: 'Agent Aadhaar Number', inputmode: 'numeric', maxlength: '12', placeholder: '12-digit Aadhaar', value: this._params.aadhaar || '', onInput: (v) => { this._params.aadhaar = v; } });
    const nameField = this.field({ label: 'Agent Name', value: this._params.customerName || '', onInput: (v) => { this._params.customerName = v; } });
    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);
    const statusSlot = h('p', { class: 'bbx-muted' });

    this.showScreen(
      h('div', {}, [
        aadhaarField.node,
        nameField.node,
        errorSlot,
        statusSlot,
        h('button', {
          class: 'bbx-btn',
          text: 'Scan Fingerprint & Authenticate',
          onClick: async (e) => {
            if (!this._params.aadhaar || this._params.aadhaar.length !== 12) {
              errorSlot.replaceChildren(this.banner('error', 'Enter a valid 12-digit Aadhaar number'));
              return;
            }
            e.target.disabled = true;
            errorSlot.replaceChildren();

            const capture = await this.runCapture(statusSlot);
            if (!capture) { this.screenAgentAuth(); return; }

            statusSlot.textContent = 'Authenticating…';
            try {
              const res = await this.client.aeps.reqAuth(this.buildPayload(capture));
              if (res.statusCode !== '00') {
                this.fail(res, res.message);
                this.screenAgentAuth(res.message || 'Authentication failed');
                return;
              }
              this.screenMenu();
            } catch (err) {
              this.fail(err);
              this.screenAgentAuth(this.errorMessage(err, 'Authentication failed'));
            }
          },
        }),
      ]),
    );
  }

  screenMenu() {
    this.setHeader({ title: 'AEPS Services', subtitle: 'Choose a service', showBack: false });
    const grid = h(
      'div',
      { class: 'bbx-select-grid' },
      OPERATIONS.map((op) =>
        h('div', { class: 'bbx-select-item', onClick: () => this.screenForm(op) }, [
          h('div', { class: 'bbx-sel-icon', text: op.icon }),
          h('div', { class: 'bbx-sel-label', text: op.label }),
        ]),
      ),
    );
    this.showScreen(h('div', {}, [grid]));
  }

  async screenForm(op, errorMsg) {
    this._params.operation = op;
    this.setHeader({ title: op.label, showBack: true, onBack: () => this.screenMenu() });
    this.renderLoading('Loading banks…');
    await this.fetchBanks();

    const aadhaarField = this.field({ label: 'Customer Aadhaar Number', inputmode: 'numeric', maxlength: '12', placeholder: '12-digit Aadhaar', value: this._params.aadhaar || '', onInput: (v) => { this._params.aadhaar = v; } });
    const nameField = this.field({ label: 'Customer Name', value: this._params.customerName || '', onInput: (v) => { this._params.customerName = v; } });

    const bankOptions = [
      h('option', { value: '', text: this._banks.length ? 'Choose issuing bank' : 'No banks available' }),
      ...this._banks.map((b) => h('option', { value: b.iin, text: b.bankName })),
    ];
    const bankSelect = h('select', { class: 'bbx-input', onChange: (e) => { this._params.bankIin = e.target.value; this._params.bankName = this._banks.find((b) => b.iin === e.target.value)?.bankName || ''; } }, bankOptions);
    if (this._params.bankIin) bankSelect.value = this._params.bankIin;
    const bankField = h('div', { class: 'bbx-field' }, [h('label', { text: 'Select Bank' }), bankSelect]);

    const amountField = op.needsAmount
      ? this.field({ label: 'Amount', type: 'number', placeholder: '0.00', value: this._params.amount ? String(this._params.amount) : '', onInput: (v) => { this._params.amount = v; } })
      : null;

    const errorSlot = h('div', {}, errorMsg ? [this.banner('error', errorMsg)] : []);
    const statusSlot = h('p', { class: 'bbx-muted' });

    const children = [aadhaarField.node, nameField.node, bankField];
    if (amountField) children.push(amountField.node);
    children.push(
      errorSlot,
      statusSlot,
      h('button', {
        class: 'bbx-btn',
        text: 'Scan Fingerprint & Submit',
        onClick: (e) => this.submit(op, e.target, errorSlot, statusSlot),
      }),
    );

    this.showScreen(h('div', {}, children));
  }

  /** Detects the RD device (if not already), gets geolocation, captures a
   *  fingerprint. Returns { pidData, lat, lng } on success, or null having
   *  already rendered the error — mirrors BiometricCapture.vue's onCapture. */
  async runCapture(statusSlot) {
    statusSlot.textContent = 'Connecting to biometric device…';
    const device = await rd.findFirstConnectedDevice();
    if (!device) {
      this.fail({ message: 'No RD biometric service found running on this machine' });
      statusSlot.textContent = '';
      return null;
    }

    statusSlot.textContent = 'Getting location…';
    const geo = await getLocation();
    if (!geo.ok) {
      this.fail({ message: geo.message });
      statusSlot.textContent = '';
      return null;
    }

    statusSlot.textContent = 'Place finger on the scanner…';
    const capture = await rd.captureFingerprint(device.vendor);
    statusSlot.textContent = '';
    if (!capture.ok) {
      this.fail({ message: capture.message });
      return null;
    }

    return { pidData: capture.pidDataXml, lat: geo.lat, lng: geo.lng };
  }

  /** Shared payload shape for every AEPS call (reqAuth included) — ported from
   *  webpayment's stores/aeps.ts#buildPayload. TxnAmount is required by the
   *  backend's validator even for balance/statement (send 0), and BankName
   *  is only included when a bank was actually selected. */
  buildPayload(capture) {
    return {
      AadharNo: this._params.aadhaar,
      TxnAmount: Number(this._params.amount) || 0,
      Latitude: capture.lat,
      Longitude: capture.lng,
      CustomerName: this._params.customerName || 'Customer',
      PidData: capture.pidData,
      BankName: this._params.bankIin || undefined,
    };
  }

  async submit(op, btn, errorSlot, statusSlot) {
    if (!this._params.aadhaar || this._params.aadhaar.length !== 12) {
      errorSlot.replaceChildren(this.banner('error', 'Enter a valid 12-digit Aadhaar number'));
      return;
    }
    if (!this._params.bankIin) {
      errorSlot.replaceChildren(this.banner('error', "Select the customer's bank"));
      return;
    }
    if (op.needsAmount && (!this._params.amount || Number(this._params.amount) <= 0)) {
      errorSlot.replaceChildren(this.banner('error', 'Enter a valid amount'));
      return;
    }

    btn.disabled = true;
    errorSlot.replaceChildren();

    const capture = await this.runCapture(statusSlot);
    if (!capture) { btn.disabled = false; return; }

    this.showOverlay(`Processing ${op.label}…`);
    try {
      const res = await this.client.aeps[op.id](this.buildPayload(capture));
      this.hideOverlay();
      this.screenResult(op, res);
    } catch (err) {
      this.hideOverlay();
      this.fail(err);
      this.screenForm(op, this.errorMessage(err, `${op.label} failed`));
    }
  }

  screenResult(op, res) {
    const result = mapAepsResponse(res, this._params.aadhaar);
    this.setHeader({ title: op.label, showBack: false });

    const rows = [
      h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Customer' }), h('span', { class: 'bbx-row-v', text: this._params.customerName || '—' })]),
      h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Aadhaar' }), h('span', { class: 'bbx-row-v bbx-mono', text: result.aadhaarMasked })]),
      this._params.bankName ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Bank' }), h('span', { class: 'bbx-row-v', text: this._params.bankName })]) : null,
      result.stan ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'STAN' }), h('span', { class: 'bbx-row-v bbx-mono', text: result.stan })]) : null,
      result.retailerReferenceNumber ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Reference Number' }), h('span', { class: 'bbx-row-v bbx-mono', text: result.retailerReferenceNumber })]) : null,
      result.authCode ? h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Auth Code' }), h('span', { class: 'bbx-row-v bbx-mono', text: String(result.authCode) })]) : null,
      h('div', { class: 'bbx-row' }, [h('span', { class: 'bbx-row-k', text: 'Date & Time' }), h('span', { class: 'bbx-row-v', text: formatDateTime(Date.now()) })]),
    ].filter(Boolean);

    const heroValue = op.id === 'balance' ? result.balance : op.needsAmount ? this._params.amount : null;

    const statementBlock = op.id === 'minStatement'
      ? h('div', { style: { marginBottom: '10px' } }, (result.statementLines?.length ? result.statementLines : ['No recent transactions returned.']).map((line) =>
          h('div', { class: 'bbx-mono', style: { fontSize: '12px', background: '#f8fafc', border: `1px solid ${BX.border}`, borderRadius: '8px', padding: '8px 10px', marginBottom: '6px' }, text: String(line) }),
        ))
      : null;

    const meta = result.status === 'SUCCESS' ? { icon: '✓', bg: BX.success2 } : result.status === 'PENDING' ? { icon: '⏳', bg: BX.warning } : { icon: '✕', bg: BX.error };
    const title = result.status === 'SUCCESS' ? `${op.label} Successful` : result.status === 'PENDING' ? `${op.label} Pending` : `${op.label} Failed`;

    const payload = { ...res, ...result, operation: op.id, aadhaar: this._params.aadhaar, amount: this._params.amount };

    // succeed()/fail() fire when the user acknowledges the result (clicks
    // Done), not the instant it's known — firing immediately here meant a
    // caller whose onSuccess/onError closes the widget (the documented
    // Checkout/bucksboxHandler pattern does exactly that) would tear it down
    // before the customer ever saw this screen. AEPS has no requery/polling
    // — NSDL either answers synchronously or it doesn't (PENDING) — and this
    // is a one-shot checkout the caller is waiting on a definitive outcome
    // from, so it always resolves to success or failure, never silently
    // neither: an unconfirmed (PENDING) result is reported as failure (can't
    // confirm the debit succeeded), not success.
    const reportResult = () => {
      if (result.status === 'SUCCESS') {
        this.succeed(payload);
      } else {
        this.fail(res, result.status === 'PENDING'
          ? 'No confirmation received from the bank — verify with the reference number before treating this as successful'
          : result.reason);
      }
    };

    this.showScreen(
      h('div', {}, [
        h('div', { class: 'bbx-status-icon', style: { background: meta.bg }, text: meta.icon }),
        h('p', { class: 'bbx-status-title', text: title }),
        result.status !== 'SUCCESS' ? h('p', { class: 'bbx-status-sub', text: result.status === 'PENDING' ? 'No confirmation received yet — check the reference number with support.' : result.reason }) : null,
        h('div', { class: 'bbx-card' }, [
          heroValue != null ? h('div', { class: 'bbx-amount-hero', text: formatCurrency(heroValue) }) : null,
          statementBlock,
          ...rows,
        ]),
        h('button', { class: 'bbx-btn', text: 'Done', onClick: () => { reportResult(); this.opts.onClose && this.opts.onClose(); } }),
      ]),
    );
  }
}

module.exports = { AepsWidget };
